'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import * as XLSX from 'xlsx';
import ToolSEO from '@/components/ToolSEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import SocialShareButtons from '@/components/SocialShareButtons';
import UniversalBookmark from '@/components/UniversalBookmark';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

// --- Types ---

interface CompBenchmark {
  role: string;
  stage: string;
  headcount: string;
  geography: string;
  base_p25: number;
  base_p50: number;
  base_p75: number;
  bonus_pct: number;
  equity_p25: number;
  equity_p50: number;
  equity_p75: number;
}

// --- Constants ---

const ROLES = [
  { id: 'cto', label: 'CTO' },
  { id: 'vp_eng', label: 'VP Engineering' },
  { id: 'svp_eng', label: 'SVP / Head of Engineering' },
  { id: 'chief_architect', label: 'Chief Architect' },
  { id: 'eng_director', label: 'Engineering Director' },
] as const;

const COMPANY_STAGES = [
  { id: 'seed', label: 'Seed / Pre-revenue' },
  { id: 'series_a', label: 'Series A' },
  { id: 'series_bc', label: 'Series B-C' },
  { id: 'series_d_late', label: 'Series D+ / Late Stage' },
  { id: 'public', label: 'Public Company' },
] as const;

const HEADCOUNTS = [
  { id: '1_20', label: '1-20 employees' },
  { id: '21_50', label: '21-50 employees' },
  { id: '51_200', label: '51-200 employees' },
  { id: '201_500', label: '201-500 employees' },
  { id: '501_2000', label: '501-2,000 employees' },
  { id: '2000_plus', label: '2,000+ employees' },
] as const;

const GEOGRAPHIES = [
  { id: 'us_sf_nyc', label: 'US (SF Bay Area / NYC)' },
  { id: 'us_major_metro', label: 'US (Other Major Metro)' },
  { id: 'us_remote', label: 'US (Remote / Other)' },
  { id: 'western_europe', label: 'Western Europe' },
  { id: 'uk', label: 'United Kingdom' },
  { id: 'apac', label: 'Asia-Pacific' },
  { id: 'latam', label: 'Latin America' },
] as const;

const CURRENCIES = [
  { code: 'USD', symbol: '$', rate: 1 },
  { code: 'EUR', symbol: '\u20AC', rate: 0.92 },
  { code: 'GBP', symbol: '\u00A3', rate: 0.79 },
] as const;

// --- Benchmark Data ---
// Based on aggregated publicly available compensation survey data including
// Levels.fyi, Glassdoor, Hired State of Tech Salaries, Kruze Consulting,
// Carta equity data, and Option Impact. All figures in USD.

// Geography multipliers relative to US SF/NYC
const GEO_MULTIPLIERS: Record<string, number> = {
  us_sf_nyc: 1.0,
  us_major_metro: 0.88,
  us_remote: 0.78,
  western_europe: 0.72,
  uk: 0.75,
  apac: 0.6,
  latam: 0.45,
};

// Headcount multipliers (larger companies generally pay more base, less equity %)
const HEADCOUNT_BASE_MULT: Record<string, number> = {
  '1_20': 0.75,
  '21_50': 0.85,
  '51_200': 0.95,
  '201_500': 1.0,
  '501_2000': 1.08,
  '2000_plus': 1.15,
};

const HEADCOUNT_EQUITY_MULT: Record<string, number> = {
  '1_20': 2.5,
  '21_50': 1.8,
  '51_200': 1.2,
  '201_500': 1.0,
  '501_2000': 0.85,
  '2000_plus': 0.7,
};

// Core benchmarks: role x stage (US SF/NYC, 201-500 headcount baseline)
interface BaseBenchmark {
  base_p25: number;
  base_p50: number;
  base_p75: number;
  bonus_pct: number; // as decimal (0.15 = 15%)
  equity_p25: number;
  equity_p50: number;
  equity_p75: number;
}

const CORE_BENCHMARKS: Record<string, Record<string, BaseBenchmark>> = {
  cto: {
    seed:           { base_p25: 140000, base_p50: 170000, base_p75: 200000, bonus_pct: 0.05, equity_p25: 150000, equity_p50: 300000, equity_p75: 600000 },
    series_a:       { base_p25: 180000, base_p50: 210000, base_p75: 250000, bonus_pct: 0.10, equity_p25: 200000, equity_p50: 400000, equity_p75: 750000 },
    series_bc:      { base_p25: 230000, base_p50: 275000, base_p75: 320000, bonus_pct: 0.15, equity_p25: 250000, equity_p50: 500000, equity_p75: 900000 },
    series_d_late:  { base_p25: 280000, base_p50: 330000, base_p75: 390000, bonus_pct: 0.20, equity_p25: 300000, equity_p50: 600000, equity_p75: 1100000 },
    public:         { base_p25: 320000, base_p50: 380000, base_p75: 450000, bonus_pct: 0.25, equity_p25: 400000, equity_p50: 800000, equity_p75: 1500000 },
  },
  vp_eng: {
    seed:           { base_p25: 130000, base_p50: 155000, base_p75: 180000, bonus_pct: 0.05, equity_p25: 100000, equity_p50: 200000, equity_p75: 400000 },
    series_a:       { base_p25: 170000, base_p50: 195000, base_p75: 230000, bonus_pct: 0.10, equity_p25: 150000, equity_p50: 300000, equity_p75: 550000 },
    series_bc:      { base_p25: 210000, base_p50: 250000, base_p75: 295000, bonus_pct: 0.15, equity_p25: 200000, equity_p50: 400000, equity_p75: 700000 },
    series_d_late:  { base_p25: 250000, base_p50: 300000, base_p75: 350000, bonus_pct: 0.18, equity_p25: 250000, equity_p50: 500000, equity_p75: 900000 },
    public:         { base_p25: 290000, base_p50: 340000, base_p75: 400000, bonus_pct: 0.22, equity_p25: 350000, equity_p50: 650000, equity_p75: 1200000 },
  },
  svp_eng: {
    seed:           { base_p25: 125000, base_p50: 150000, base_p75: 175000, bonus_pct: 0.05, equity_p25: 90000,  equity_p50: 180000, equity_p75: 350000 },
    series_a:       { base_p25: 160000, base_p50: 185000, base_p75: 215000, bonus_pct: 0.08, equity_p25: 130000, equity_p50: 250000, equity_p75: 480000 },
    series_bc:      { base_p25: 200000, base_p50: 240000, base_p75: 280000, bonus_pct: 0.13, equity_p25: 180000, equity_p50: 350000, equity_p75: 650000 },
    series_d_late:  { base_p25: 240000, base_p50: 285000, base_p75: 335000, bonus_pct: 0.17, equity_p25: 220000, equity_p50: 450000, equity_p75: 800000 },
    public:         { base_p25: 275000, base_p50: 325000, base_p75: 385000, bonus_pct: 0.22, equity_p25: 320000, equity_p50: 600000, equity_p75: 1100000 },
  },
  chief_architect: {
    seed:           { base_p25: 135000, base_p50: 160000, base_p75: 190000, bonus_pct: 0.05, equity_p25: 80000,  equity_p50: 160000, equity_p75: 320000 },
    series_a:       { base_p25: 170000, base_p50: 200000, base_p75: 235000, bonus_pct: 0.08, equity_p25: 120000, equity_p50: 240000, equity_p75: 450000 },
    series_bc:      { base_p25: 210000, base_p50: 250000, base_p75: 290000, bonus_pct: 0.12, equity_p25: 160000, equity_p50: 320000, equity_p75: 600000 },
    series_d_late:  { base_p25: 250000, base_p50: 300000, base_p75: 350000, bonus_pct: 0.15, equity_p25: 200000, equity_p50: 400000, equity_p75: 750000 },
    public:         { base_p25: 290000, base_p50: 345000, base_p75: 410000, bonus_pct: 0.20, equity_p25: 300000, equity_p50: 550000, equity_p75: 1000000 },
  },
  eng_director: {
    seed:           { base_p25: 120000, base_p50: 140000, base_p75: 165000, bonus_pct: 0.05, equity_p25: 60000,  equity_p50: 120000, equity_p75: 250000 },
    series_a:       { base_p25: 150000, base_p50: 175000, base_p75: 205000, bonus_pct: 0.08, equity_p25: 90000,  equity_p50: 180000, equity_p75: 350000 },
    series_bc:      { base_p25: 185000, base_p50: 220000, base_p75: 260000, bonus_pct: 0.12, equity_p25: 130000, equity_p50: 260000, equity_p75: 480000 },
    series_d_late:  { base_p25: 220000, base_p50: 265000, base_p75: 310000, bonus_pct: 0.15, equity_p25: 170000, equity_p50: 340000, equity_p75: 620000 },
    public:         { base_p25: 255000, base_p50: 305000, base_p75: 360000, bonus_pct: 0.20, equity_p25: 250000, equity_p50: 480000, equity_p75: 850000 },
  },
};

// --- Component ---

const TOOL_NAME = 'CTO Compensation Benchmark';
const TOOL_SLUG = 'cto-compensation-benchmark';
const TOOL_DESCRIPTION = 'Research-backed salary and total compensation benchmarks for CTOs and tech executives by company stage, size, and geography';

export default function CTOCompensationBenchmarkPage() {
  const [role, setRole] = useState('cto');
  const [stage, setStage] = useState('series_bc');
  const [headcount, setHeadcount] = useState('51_200');
  const [geography, setGeography] = useState('us_sf_nyc');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [saved, setSaved] = useState(false);

  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'cto-compensation-benchmark',
    toolCategory: 'calculator',
  });

  const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];

  const benchmark = useMemo((): CompBenchmark => {
    const core = CORE_BENCHMARKS[role]?.[stage];
    if (!core) {
      return {
        role, stage, headcount, geography,
        base_p25: 0, base_p50: 0, base_p75: 0,
        bonus_pct: 0,
        equity_p25: 0, equity_p50: 0, equity_p75: 0,
      };
    }

    const geoMult = GEO_MULTIPLIERS[geography] || 1;
    const hcBaseMult = HEADCOUNT_BASE_MULT[headcount] || 1;
    const hcEqMult = HEADCOUNT_EQUITY_MULT[headcount] || 1;

    const round5k = (n: number) => Math.round(n / 5000) * 5000;

    return {
      role,
      stage,
      headcount,
      geography,
      base_p25: round5k(core.base_p25 * geoMult * hcBaseMult),
      base_p50: round5k(core.base_p50 * geoMult * hcBaseMult),
      base_p75: round5k(core.base_p75 * geoMult * hcBaseMult),
      bonus_pct: core.bonus_pct,
      equity_p25: round5k(core.equity_p25 * geoMult * hcEqMult),
      equity_p50: round5k(core.equity_p50 * geoMult * hcEqMult),
      equity_p75: round5k(core.equity_p75 * geoMult * hcEqMult),
    };
  }, [role, stage, headcount, geography]);

  // Comparison across stages for the selected role + geo + headcount
  const stageComparison = useMemo(() => {
    return COMPANY_STAGES.map(s => {
      const core = CORE_BENCHMARKS[role]?.[s.id];
      if (!core) return null;
      const geoMult = GEO_MULTIPLIERS[geography] || 1;
      const hcBaseMult = HEADCOUNT_BASE_MULT[headcount] || 1;
      const hcEqMult = HEADCOUNT_EQUITY_MULT[headcount] || 1;
      const round5k = (n: number) => Math.round(n / 5000) * 5000;
      const base = round5k(core.base_p50 * geoMult * hcBaseMult);
      const bonus = Math.round(base * core.bonus_pct);
      const equity = round5k(core.equity_p50 * geoMult * hcEqMult);
      return {
        stage: s.label,
        stageId: s.id,
        base,
        bonus,
        equity,
        total: base + bonus + equity,
      };
    }).filter(Boolean) as { stage: string; stageId: string; base: number; bonus: number; equity: number; total: number }[];
  }, [role, geography, headcount]);

  // Generate insights
  const insights = useMemo(() => {
    const items: string[] = [];
    const roleLabel = ROLES.find(r => r.id === role)?.label || role;
    const geoLabel = GEOGRAPHIES.find(g => g.id === geography)?.label || geography;

    // Equity vs base ratio
    if (benchmark.equity_p50 > 0) {
      const ratio = benchmark.equity_p50 / benchmark.base_p50;
      if (ratio > 2) {
        items.push(`Equity is ${ratio.toFixed(1)}x base salary at this stage, reflecting high growth potential and risk.`);
      } else if (ratio > 1) {
        items.push(`Equity (${formatUSD(benchmark.equity_p50)}) exceeds base salary, typical for venture-backed companies at this stage.`);
      } else {
        items.push(`Base salary is the dominant component at ${Math.round((benchmark.base_p50 / (benchmark.base_p50 + benchmark.equity_p50)) * 100)}% of cash + equity.`);
      }
    }

    // Stage comparison insight
    const seriesA = CORE_BENCHMARKS[role]?.series_a;
    const seriesBC = CORE_BENCHMARKS[role]?.series_bc;
    if (seriesA && seriesBC) {
      const equityJump = seriesBC.equity_p50 / seriesA.equity_p50;
      items.push(`${roleLabel}s at Series B-C earn ${equityJump.toFixed(1)}x more equity than at Series A.`);
    }

    // Geography insight
    const geoMult = GEO_MULTIPLIERS[geography] || 1;
    if (geoMult < 0.8) {
      const sfBase = CORE_BENCHMARKS[role]?.[stage]?.base_p50 || 0;
      const discount = Math.round((1 - geoMult) * 100);
      items.push(`${geoLabel} compensation is ~${discount}% below SF/NYC. A ${roleLabel} here earns ${formatUSD(Math.round(sfBase * geoMult / 5000) * 5000)} vs ${formatUSD(sfBase)} in top US metros.`);
    }

    // P25-P75 spread
    const spread = benchmark.base_p75 - benchmark.base_p25;
    const spreadPct = Math.round((spread / benchmark.base_p50) * 100);
    items.push(`The P25-P75 base salary range spans ${formatUSD(spread)} (${spreadPct}% of median), reflecting variation by individual experience and company performance.`);

    return items;
  }, [benchmark, role, stage, geography]);

  function formatUSD(amount: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  }

  function formatAmount(amount: number): string {
    const converted = Math.round(amount * currency.rate);
    if (currency.code === 'USD') return formatUSD(amount);
    return `${currency.symbol}${converted.toLocaleString()}`;
  }

  function formatCompact(amount: number): string {
    const converted = Math.round(amount * currency.rate);
    if (converted >= 1000000) return `${currency.symbol}${(converted / 1000000).toFixed(1)}M`;
    if (converted >= 1000) return `${currency.symbol}${Math.round(converted / 1000)}K`;
    return `${currency.symbol}${converted.toLocaleString()}`;
  }

  const bonus_p50 = Math.round(benchmark.base_p50 * benchmark.bonus_pct);
  const totalComp_p25 = benchmark.base_p25 + Math.round(benchmark.base_p25 * benchmark.bonus_pct) + benchmark.equity_p25;
  const totalComp_p50 = benchmark.base_p50 + bonus_p50 + benchmark.equity_p50;
  const totalComp_p75 = benchmark.base_p75 + Math.round(benchmark.base_p75 * benchmark.bonus_pct) + benchmark.equity_p75;

  // Percentile bar helper
  function PercentileBar({ p25, p50, p75, label }: { p25: number; p50: number; p75: number; label: string }) {
    const max = p75 * 1.1;
    const p25Pct = (p25 / max) * 100;
    const p50Pct = (p50 / max) * 100;
    const p75Pct = (p75 / max) * 100;

    return (
      <div className="mb-5">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-semibold text-foreground">{label}</span>
          <span className="text-sm text-foreground-tertiary">P50: {formatCompact(p50)}</span>
        </div>
        <div className="relative h-8 bg-background rounded-full overflow-hidden border border-border-muted">
          {/* P25-P75 range */}
          <div
            className="absolute h-full bg-accent-muted rounded-full"
            style={{ left: `${p25Pct}%`, width: `${p75Pct - p25Pct}%` }}
          />
          {/* P50 marker */}
          <div
            className="absolute h-full w-1 bg-accent rounded-full"
            style={{ left: `${p50Pct}%` }}
          />
          {/* P25 label */}
          <div className="absolute top-1/2 -translate-y-1/2 text-xs text-foreground-tertiary" style={{ left: `${Math.max(1, p25Pct - 1)}%` }}>
            {formatCompact(p25)}
          </div>
          {/* P75 label */}
          <div className="absolute top-1/2 -translate-y-1/2 text-xs text-foreground-tertiary text-right" style={{ right: `${Math.max(1, 100 - p75Pct - 1)}%` }}>
            {formatCompact(p75)}
          </div>
        </div>
      </div>
    );
  }

  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const roleLabel = ROLES.find(r => r.id === role)?.label || role;
    const stageLabel = COMPANY_STAGES.find(s => s.id === stage)?.label || stage;

    const assessment = await saveResult({
      name: `Comp Benchmark - ${roleLabel} at ${stageLabel} - ${new Date().toLocaleDateString()}`,
      score_overall: totalComp_p50,
      scores: {
        'Base P50': benchmark.base_p50,
        'Bonus P50': bonus_p50,
        'Equity P50': benchmark.equity_p50,
        'Total Comp P50': totalComp_p50,
      },
      answers: {
        role,
        stage,
        headcount,
        geography,
        currency: currencyCode,
      },
      metadata: {
        base: { p25: benchmark.base_p25, p50: benchmark.base_p50, p75: benchmark.base_p75 },
        bonus_pct: benchmark.bonus_pct,
        equity: { p25: benchmark.equity_p25, p50: benchmark.equity_p50, p75: benchmark.equity_p75 },
        totalComp: { p25: totalComp_p25, p50: totalComp_p50, p75: totalComp_p75 },
        stageComparison,
      },
    });

    if (assessment) setSaved(true);
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    if (!wb.Workbook) wb.Workbook = {};
    (wb.Workbook as { CalcPr?: { fullCalcOnLoad: boolean } }).CalcPr = { fullCalcOnLoad: true };

    const roleLabel = ROLES.find(r => r.id === role)?.label || role;
    const stageLabel = COMPANY_STAGES.find(s => s.id === stage)?.label || stage;
    const hcLabel = HEADCOUNTS.find(h => h.id === headcount)?.label || headcount;
    const geoLabel = GEOGRAPHIES.find(g => g.id === geography)?.label || geography;

    const summaryData = [
      ['CTO Compensation Benchmark Report'],
      ['Generated:', new Date().toLocaleDateString()],
      [],
      ['Parameters'],
      ['Role:', roleLabel],
      ['Company Stage:', stageLabel],
      ['Company Headcount:', hcLabel],
      ['Geography:', geoLabel],
      ['Currency:', currencyCode],
      [],
      ['Base Salary'],
      ['P25:', formatAmount(benchmark.base_p25)],
      ['P50 (Median):', formatAmount(benchmark.base_p50)],
      ['P75:', formatAmount(benchmark.base_p75)],
      [],
      ['Bonus'],
      ['Target Bonus %:', `${Math.round(benchmark.bonus_pct * 100)}%`],
      ['Bonus at P50 Base:', formatAmount(bonus_p50)],
      [],
      ['Equity (Annual Value)'],
      ['P25:', formatAmount(benchmark.equity_p25)],
      ['P50 (Median):', formatAmount(benchmark.equity_p50)],
      ['P75:', formatAmount(benchmark.equity_p75)],
      [],
      ['Total Compensation (Base + Bonus + Equity)'],
      ['P25:', formatAmount(totalComp_p25)],
      ['P50 (Median):', formatAmount(totalComp_p50)],
      ['P75:', formatAmount(totalComp_p75)],
      [],
      ['Methodology'],
      ['Data sources: Aggregated from Levels.fyi, Glassdoor, Hired State of Tech Salaries,'],
      ['Kruze Consulting CTO pay research, Carta equity data, and Option Impact.'],
      ['Equity values represent estimated annualized value (4-year vesting).'],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // Stage comparison sheet
    const compData = [
      ['Stage Comparison', '', '', '', ''],
      ['Stage', `Base (${currencyCode})`, `Bonus (${currencyCode})`, `Equity (${currencyCode})`, `Total (${currencyCode})`],
      ...stageComparison.map(s => [
        s.stage,
        formatAmount(s.base),
        formatAmount(s.bonus),
        formatAmount(s.equity),
        formatAmount(s.total),
      ]),
    ];

    const compSheet = XLSX.utils.aoa_to_sheet(compData);
    XLSX.utils.book_append_sheet(wb, compSheet, 'Stage Comparison');

    XLSX.writeFile(wb, `cto-comp-benchmark-${roleLabel.replace(/\s+/g, '-')}-${stageLabel.replace(/[\s/]+/g, '-')}-${Date.now()}.xlsx`);
  };

  return (
    <>
      <ToolSEO
        toolName={TOOL_NAME}
        toolSlug={TOOL_SLUG}
        description={TOOL_DESCRIPTION}
        category="calculator"
      />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <Breadcrumbs
            items={[
              { name: 'Tools', url: '/tools' },
              { name: TOOL_NAME, url: `/tools/${TOOL_SLUG}` },
            ]}
          />

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">💼</span>
                  <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                    {TOOL_NAME}
                  </h1>
                </div>
                <p className="text-xl text-foreground-secondary">
                  {TOOL_DESCRIPTION}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <UniversalBookmark
                  contentType="tool"
                  contentSlug={TOOL_SLUG}
                  contentTitle={TOOL_NAME}
                  variant="icon"
                />
                <SocialShareButtons
                  url={`https://theartofcto.com/tools/${TOOL_SLUG}`}
                  title={TOOL_NAME}
                  description={TOOL_DESCRIPTION}
                  variant="dropdown"
                />
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
            <h3 className="font-bold text-accent mb-2">How to use this tool</h3>
            <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
              <li>Select the executive role you want to benchmark</li>
              <li>Choose the company stage, headcount, and geography that match your situation</li>
              <li>Review percentile-based salary bands with equity and bonus breakdowns</li>
              <li>Compare how compensation changes across company stages</li>
              <li>Export to Excel for your board deck or compensation planning</li>
            </ol>
            <p className="text-xs text-foreground-tertiary mt-4">
              Data aggregated from Levels.fyi, Glassdoor, Hired, Kruze Consulting, Carta, and Option Impact surveys.
              Equity values represent estimated annualized value assuming standard 4-year vesting.
            </p>
          </div>

          {/* Grid: Inputs + Results */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column: Inputs */}
            <div className="space-y-6">
              <div className="bg-surface rounded-lg border border-border p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">Configuration</h3>
                <div className="space-y-4">
                  {/* Role */}
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">Executive Role</label>
                    <select
                      value={role}
                      onChange={e => { setRole(e.target.value); setSaved(false); }}
                      className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                    >
                      {ROLES.map(r => (
                        <option key={r.id} value={r.id} className="text-gray-900 bg-white">{r.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Company Stage */}
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">Company Stage</label>
                    <select
                      value={stage}
                      onChange={e => { setStage(e.target.value); setSaved(false); }}
                      className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                    >
                      {COMPANY_STAGES.map(s => (
                        <option key={s.id} value={s.id} className="text-gray-900 bg-white">{s.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Headcount */}
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">Company Headcount</label>
                    <select
                      value={headcount}
                      onChange={e => { setHeadcount(e.target.value); setSaved(false); }}
                      className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                    >
                      {HEADCOUNTS.map(h => (
                        <option key={h.id} value={h.id} className="text-gray-900 bg-white">{h.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Geography */}
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">Geography</label>
                    <select
                      value={geography}
                      onChange={e => { setGeography(e.target.value); setSaved(false); }}
                      className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                    >
                      {GEOGRAPHIES.map(g => (
                        <option key={g.id} value={g.id} className="text-gray-900 bg-white">{g.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">Display Currency</label>
                    <select
                      value={currencyCode}
                      onChange={e => setCurrencyCode(e.target.value)}
                      className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                    >
                      {CURRENCIES.map(c => (
                        <option key={c.code} value={c.code} className="text-gray-900 bg-white">{c.symbol} {c.code}</option>
                      ))}
                    </select>
                    <p className="text-xs text-foreground-tertiary mt-1">All benchmarks are in USD, converted at approximate rates</p>
                  </div>
                </div>
              </div>

              {/* Key Insights */}
              <div className="bg-surface rounded-lg border border-border p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">Key Insights</h3>
                <ul className="space-y-3">
                  {insights.map((insight, i) => (
                    <li key={i} className="flex gap-2 text-sm text-foreground-secondary">
                      <span className="text-accent shrink-0 mt-0.5">&#x25B8;</span>
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Column: Results */}
            <div className="space-y-6">
              {/* Total Comp Overview */}
              <div className="bg-surface rounded-lg border border-border p-6">
                <h3 className="text-xl font-bold text-foreground mb-2">Total Compensation</h3>
                <p className="text-sm text-foreground-tertiary mb-4">
                  {ROLES.find(r => r.id === role)?.label} &middot; {COMPANY_STAGES.find(s => s.id === stage)?.label} &middot; {GEOGRAPHIES.find(g => g.id === geography)?.label}
                </p>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-xs text-foreground-tertiary mb-1">P25</div>
                    <div className="text-lg font-bold text-foreground">{formatCompact(totalComp_p25)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-accent mb-1">P50 (Median)</div>
                    <div className="text-2xl font-bold text-accent">{formatCompact(totalComp_p50)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-foreground-tertiary mb-1">P75</div>
                    <div className="text-lg font-bold text-foreground">{formatCompact(totalComp_p75)}</div>
                  </div>
                </div>

                {/* Percentile bars */}
                <PercentileBar label="Base Salary" p25={benchmark.base_p25} p50={benchmark.base_p50} p75={benchmark.base_p75} />
                <PercentileBar label={`Bonus (${Math.round(benchmark.bonus_pct * 100)}% target)`} p25={Math.round(benchmark.base_p25 * benchmark.bonus_pct)} p50={bonus_p50} p75={Math.round(benchmark.base_p75 * benchmark.bonus_pct)} />
                <PercentileBar label="Equity (Annual)" p25={benchmark.equity_p25} p50={benchmark.equity_p50} p75={benchmark.equity_p75} />
              </div>

              {/* Detailed Breakdown */}
              <div className="bg-surface rounded-lg border border-border p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">P50 Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground-secondary">Base Salary</span>
                    <span className="text-lg font-bold text-foreground">{formatAmount(benchmark.base_p50)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-foreground-secondary">Target Bonus ({Math.round(benchmark.bonus_pct * 100)}%)</span>
                    <span className="text-lg font-bold text-foreground">{formatAmount(bonus_p50)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-foreground-secondary">Equity (Annual)</span>
                    <span className="text-lg font-bold text-foreground">{formatAmount(benchmark.equity_p50)}</span>
                  </div>
                  <div className="border-t border-border pt-3 flex justify-between items-center">
                    <span className="font-semibold text-foreground">Total Compensation</span>
                    <span className="text-2xl font-bold text-accent">{formatAmount(totalComp_p50)}</span>
                  </div>
                </div>

                {/* Composition bar */}
                {totalComp_p50 > 0 && (
                  <div className="mt-4">
                    <div className="text-xs text-foreground-tertiary mb-1">Compensation mix</div>
                    <div className="flex h-4 rounded-full overflow-hidden">
                      <div
                        className="bg-accent"
                        style={{ width: `${(benchmark.base_p50 / totalComp_p50) * 100}%` }}
                        title={`Base: ${Math.round((benchmark.base_p50 / totalComp_p50) * 100)}%`}
                      />
                      <div
                        className="bg-accent/60"
                        style={{ width: `${(bonus_p50 / totalComp_p50) * 100}%` }}
                        title={`Bonus: ${Math.round((bonus_p50 / totalComp_p50) * 100)}%`}
                      />
                      <div
                        className="bg-accent/30"
                        style={{ width: `${(benchmark.equity_p50 / totalComp_p50) * 100}%` }}
                        title={`Equity: ${Math.round((benchmark.equity_p50 / totalComp_p50) * 100)}%`}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-foreground-tertiary">
                      <span>Base {Math.round((benchmark.base_p50 / totalComp_p50) * 100)}%</span>
                      <span>Bonus {Math.round((bonus_p50 / totalComp_p50) * 100)}%</span>
                      <span>Equity {Math.round((benchmark.equity_p50 / totalComp_p50) * 100)}%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Stage Comparison Table */}
              <div className="bg-surface rounded-lg border border-border p-6">
                <h3 className="text-xl font-bold text-foreground mb-4">Comparison by Stage</h3>
                <p className="text-sm text-foreground-tertiary mb-4">P50 total compensation for {ROLES.find(r => r.id === role)?.label} across company stages</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-foreground-secondary font-semibold">Stage</th>
                        <th className="text-right py-2 text-foreground-secondary font-semibold">Base</th>
                        <th className="text-right py-2 text-foreground-secondary font-semibold">Bonus</th>
                        <th className="text-right py-2 text-foreground-secondary font-semibold">Equity</th>
                        <th className="text-right py-2 text-foreground-secondary font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stageComparison.map(row => (
                        <tr
                          key={row.stageId}
                          className={`border-b border-border-muted ${row.stageId === stage ? 'bg-accent-muted' : ''}`}
                        >
                          <td className="py-2 text-foreground">{row.stage}</td>
                          <td className="py-2 text-right text-foreground-secondary">{formatCompact(row.base)}</td>
                          <td className="py-2 text-right text-foreground-secondary">{formatCompact(row.bonus)}</td>
                          <td className="py-2 text-right text-foreground-secondary">{formatCompact(row.equity)}</td>
                          <td className="py-2 text-right font-semibold text-foreground">{formatCompact(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={exportToExcel}
                  className="bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-colors"
                >
                  Export to Excel
                </button>

                {commandCenterEnabled && !saved && (
                  <button
                    onClick={handleSaveToCommandCenter}
                    disabled={saving}
                    className="bg-cream/10 text-foreground px-6 py-3 rounded-lg font-semibold hover:bg-cream/20 transition-colors border border-border disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save to Command Center'}
                  </button>
                )}
                {saved && (
                  <div className="bg-green-600/20 text-green-400 px-6 py-3 rounded-lg font-semibold border border-green-600/30">
                    Saved to Command Center
                  </div>
                )}
                {saveError && (
                  <div className="bg-red-600/20 text-red-400 px-6 py-3 rounded-lg text-sm border border-red-600/30">
                    {saveError}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Methodology */}
          <div className="mt-8 bg-surface rounded-lg border border-border p-6">
            <h3 className="text-xl font-bold text-foreground mb-3">Methodology</h3>
            <div className="text-sm text-foreground-secondary space-y-2">
              <p>
                Benchmarks are aggregated from multiple publicly available compensation data sources including
                Levels.fyi, Glassdoor, Hired State of Tech Salaries, Kruze Consulting CTO compensation research,
                Carta equity benchmarking, and Option Impact surveys. Data reflects 2024-2025 compensation trends.
              </p>
              <p>
                <strong className="text-foreground">Base salary</strong> reflects annual cash compensation before bonuses.
                <strong className="text-foreground"> Bonus</strong> represents target annual bonus as a percentage of base.
                <strong className="text-foreground"> Equity</strong> represents estimated annualized value of stock options or RSUs
                assuming standard 4-year vesting with a 1-year cliff.
              </p>
              <p>
                Geography adjustments use cost-of-labor indices (not cost-of-living) that reflect actual market rates for
                executive talent in each region. Company headcount adjustments reflect the tendency for larger companies to
                offer higher base salaries but lower equity percentages.
              </p>
            </div>
          </div>

          {/* Related Resources */}
          <div className="mt-8 bg-surface rounded-lg border border-border p-6">
            <h3 className="text-xl font-bold text-foreground mb-3">Related Resources</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link href="/tools/salary-band-calculator" className="text-accent hover:text-accent transition-colors text-sm">
                Salary Band Calculator &rarr;
              </Link>
              <Link href="/tools/headcount-forecasting" className="text-accent hover:text-accent transition-colors text-sm">
                Headcount Forecasting &rarr;
              </Link>
              <Link href="/tools/engineering-ladder" className="text-accent hover:text-accent transition-colors text-sm">
                Engineering Ladder Builder &rarr;
              </Link>
              <Link href="/tools/team-scaling-calculator" className="text-accent hover:text-accent transition-colors text-sm">
                Team Scaling Calculator &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
